/**
 * Plain-function controller that mediates starting chat sessions. Enforces the
 * concurrent-chat cap and same-participant dedupe at the store boundary so the
 * UI never silently creates a duplicate or a 6th chat. On success it registers
 * the session in the store, starts the live WebRTC session in the module-level
 * {@link sessionManager}, and brings it to the foreground.
 *
 * @module features/p2p-chat/lib/chatController
 */

import { useP2PChatStore } from '../stores/p2pChatStore';
import * as sessionManager from './sessionManager';

export interface StartChatOptions {
  readonly roomCode: string;
  readonly initiator: boolean;
  /** Roster id of the peer when known (invite/favorite). */
  readonly peerId?: string | null;
  readonly peerName?: string | null;
  readonly relayOnly?: boolean;
}

/** Discriminated result so callers can prompt on cap/duplicate. */
export type StartChatResult =
  | { readonly kind: 'started'; readonly id: string }
  | { readonly kind: 'cap' }
  | { readonly kind: 'duplicate'; readonly existingId: string };

/**
 * Start (or reject) a chat session.
 *   - `duplicate`: an active chat with the same participant/room already exists.
 *   - `cap`: 5 chats are already active.
 *   - `started`: a new foreground session was created.
 */
export function startChatSession(opts: StartChatOptions): StartChatResult {
  const store = useP2PChatStore.getState();
  const peerId = opts.peerId ?? null;

  const existing = store.findByParticipants(peerId, opts.roomCode);
  if (existing) return { kind: 'duplicate', existingId: existing.id };

  if (!store.canAddSession()) return { kind: 'cap' };

  const relayOnly = opts.relayOnly ?? store.relayOnly;
  const id = store.addSession({
    roomCode: opts.roomCode,
    peerId,
    peerName: opts.peerName ?? null,
    initiator: opts.initiator,
    relayOnly,
  });
  sessionManager.start({
    id,
    roomCode: opts.roomCode,
    initiator: opts.initiator,
    relayOnly,
  });
  store.setForeground(id);
  return { kind: 'started', id };
}

/** Close a chat: remove it from the store and tear down the live session. */
export function closeChatSession(id: string): void {
  useP2PChatStore.getState().closeSession(id);
  sessionManager.close(id);
}
